import { createPortal } from "react-dom"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"

/**
 * App-wide toaster.
 *
 * Two things here are load-bearing and easy to undo by accident.
 *
 * 1. THE PORTAL. Sonner renders where it is mounted — it does not portal
 *    itself. Ours is mounted inside `#root`, and `#root` carries
 *    `position: relative; z-index: 1` (App.css, so the fixed grid drawn
 *    on `body::before` stays behind the app). That pair makes `#root` a
 *    stacking context, which traps sonner's own `z-index: 999999999`
 *    INSIDE it — the number stops competing with the rest of the page
 *    and only orders toasts against their siblings. Radix dialogs, alert
 *    dialogs, sheets and vaul drawers all portal to `document.body` at
 *    `z-50`, so the browser compared `#root`'s 1 against their 50 and
 *    painted every toast under every overlay. Errors raised while a
 *    modal was open were invisible.
 *
 *    Portalling to `document.body` makes the toaster a SIBLING of those
 *    overlays instead of a descendant of `#root`, so its z-index finally
 *    means what it says. This is why the fix is one portal rather than a
 *    z-index bumped in a dozen components.
 *
 * 2. THE ERROR VARIANT. `classNames.toast` applies to every toast, so
 *    styling it alone made a failure look exactly like a success — same
 *    background, same border, same text colour. `classNames.error` is
 *    applied on top for `toast.error(...)` only. The `!` matters: both
 *    class sets land on the element with equal specificity, so without
 *    it the winner would be whichever Tailwind emitted last.
 */
const Toaster = ({
  ...props
}) => {
  const { theme = "system" } = useTheme()

  if (typeof document === "undefined") return null

  return createPortal(
    <Sonner
      theme={theme}
      className="toaster group"
      // A failure the user has to read and act on, not a passing
      // confirmation — worth a few more seconds, and a close button so
      // it is dismissed when they are done rather than on a timer.
      duration={6000}
      closeButton
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          error:
            "group-[.toaster]:!bg-destructive group-[.toaster]:!text-destructive-foreground group-[.toaster]:!border-destructive group-[.toaster]:[&_[data-description]]:!text-destructive-foreground/90 group-[.toaster]:[&_[data-close-button]]:!bg-destructive group-[.toaster]:[&_[data-close-button]]:!text-destructive-foreground group-[.toaster]:[&_[data-close-button]]:!border-destructive-foreground/40",
        },
      }}
      {...props} />,
    document.body
  );
}

export { Toaster, toast }
